(() => {
  'use strict';

  /*
    Hybrid HERO patch v2
    - Keep HARU / COLOR / final scene / typography scroll-linked
    - Move HERO fragment motion entirely to CSS animation
    - Pause HERO work outside the HERO vicinity
    - Support deferred HERO assets via data-hero-src/data-hero-srcset
  */

  const ROOT_SELECTOR = '[data-dotd-universe-root]';
  const INITIALIZED_ATTRIBUTE = 'data-dotd-universe-initialized';

  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  );
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const controllers = new WeakMap();

  const clamp = (value, minimum = 0, maximum = 1) =>
    Math.min(maximum, Math.max(minimum, value));

  const range = (value, start, end) =>
    clamp((value - start) / Math.max(0.0001, end - start));

  const smooth = (value) => value * value * (3 - 2 * value);

  const getScrollTop = () =>
    window.scrollY ??
    document.scrollingElement?.scrollTop ??
    document.documentElement.scrollTop ??
    0;

  const updateMotionPreference = (root) => {
    root.dataset.reducedMotion = reducedMotionQuery.matches
      ? 'true'
      : 'false';
  };

  const createListenerRegistry = () => {
    const cleanups = [];

    const listen = (target, eventName, handler, options) => {
      if (!target) return;

      if (target.addEventListener) {
        target.addEventListener(eventName, handler, options);

        cleanups.push(() => {
          target.removeEventListener(eventName, handler, options);
        });

        return;
      }

      if (eventName === 'change' && target.addListener) {
        target.addListener(handler);

        cleanups.push(() => {
          target.removeListener(handler);
        });
      }
    };

    const destroy = () => {
      cleanups.splice(0).forEach((cleanup) => cleanup());
    };

    return {
      listen,
      destroy,
    };
  };

  /* ==========================================================================
     Header
     ========================================================================== */

  const createHeaderController = (root) => {
    const header = root.querySelector(
      '[data-dotd-universe-header]',
    );

    if (!header) return null;

    const {
      listen,
      destroy: destroyListeners,
    } = createListenerRegistry();

    const menuToggle = header.querySelector(
      '[data-dotd-universe-menu-toggle]',
    );

    const mobileMenu = header.querySelector(
      '[data-dotd-universe-mobile-menu]',
    );

    const menuOverlay = header.querySelector(
      '[data-dotd-universe-menu-overlay]',
    );

    const navLinks = [
      ...header.querySelectorAll(
        '[data-dotd-universe-target]',
      ),
    ];

    const mobileLinks = [
      ...header.querySelectorAll(
        '.dotd-universe-header__mobile-link',
      ),
    ];

    const sectionIds = [
      ...new Set(
        navLinks
          .map((link) => link.dataset.dotdUniverseTarget)
          .filter(Boolean),
      ),
    ];

    const observedSections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const sectionVisibility = new Map();

    let intersectionObserver = null;
    let scrollFrame = null;
    let menuOpen = false;

    const setActiveSection = (activeId = '') => {
      navLinks.forEach((link) => {
        const isActive =
          link.dataset.dotdUniverseTarget === activeId;

        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    };

    const updateScrolledState = () => {
      scrollFrame = null;

      header.classList.toggle(
        'is-scrolled',
        getScrollTop() >= 80,
      );
    };

    const requestScrollUpdate = () => {
      if (scrollFrame !== null) return;

      scrollFrame =
        window.requestAnimationFrame(updateScrolledState);
    };

    const updateActiveFromPosition = () => {
      const headerHeight =
        header.getBoundingClientRect().height;

      const marker =
        headerHeight + window.innerHeight * 0.28;

      const activeSection = observedSections.find(
        (section) => {
          const bounds =
            section.getBoundingClientRect();

          return (
            bounds.top <= marker &&
            bounds.bottom > marker
          );
        },
      );

      setActiveSection(activeSection?.id || '');
    };

    const updateActiveFromObserver = () => {
      const visibleSections = observedSections
        .filter(
          (section) =>
            sectionVisibility.get(section)?.isIntersecting,
        )
        .sort((first, second) => {
          const firstEntry =
            sectionVisibility.get(first);

          const secondEntry =
            sectionVisibility.get(second);

          const ratioDifference =
            (secondEntry?.intersectionRatio || 0) -
            (firstEntry?.intersectionRatio || 0);

          return (
            ratioDifference ||
            first.getBoundingClientRect().top -
              second.getBoundingClientRect().top
          );
        });

      setActiveSection(visibleSections[0]?.id || '');
    };

    const closeMenu = ({
      restoreFocus = false,
    } = {}) => {
      if (
        !menuToggle ||
        !mobileMenu ||
        !menuOverlay
      ) {
        return;
      }

      menuOpen = false;

      header.classList.remove('is-menu-open');

      menuToggle.setAttribute(
        'aria-expanded',
        'false',
      );

      menuToggle.setAttribute(
        'aria-label',
        menuToggle.dataset.openLabel ||
          'メニューを開く',
      );

      mobileMenu.hidden = true;
      menuOverlay.hidden = true;

      document.body.classList.remove(
        'dotd-universe-menu-open',
      );

      if (restoreFocus) {
        menuToggle.focus();
      }
    };

    const openMenu = () => {
      if (
        !menuToggle ||
        !mobileMenu ||
        !menuOverlay ||
        desktopQuery.matches
      ) {
        return;
      }

      menuOpen = true;

      header.classList.add('is-menu-open');

      menuToggle.setAttribute(
        'aria-expanded',
        'true',
      );

      menuToggle.setAttribute(
        'aria-label',
        menuToggle.dataset.closeLabel ||
          'メニューを閉じる',
      );

      mobileMenu.hidden = false;
      menuOverlay.hidden = false;

      document.body.classList.add(
        'dotd-universe-menu-open',
      );

      window.requestAnimationFrame(() => {
        mobileLinks[0]?.focus();
      });
    };

    const handleMenuToggle = () => {
      if (menuOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    const handleDocumentPointerDown = (event) => {
      if (
        menuOpen &&
        !header.contains(event.target)
      ) {
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (!menuOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();

        closeMenu({
          restoreFocus: true,
        });

        return;
      }

      if (event.key !== 'Tab') return;

      const focusableItems = [
        menuToggle,
        ...mobileLinks,
      ].filter(
        (item) => item && !item.hidden,
      );

      if (!focusableItems.length) return;

      const firstItem = focusableItems[0];
      const lastItem =
        focusableItems[
          focusableItems.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstItem
      ) {
        event.preventDefault();
        lastItem.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastItem
      ) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    const handleDesktopChange = (event) => {
      if (event.matches) {
        closeMenu();
      }
    };

    listen(
      window,
      'scroll',
      requestScrollUpdate,
      {
        passive: true,
      },
    );

    listen(
      document,
      'pointerdown',
      handleDocumentPointerDown,
    );

    listen(
      document,
      'keydown',
      handleKeyDown,
    );

    listen(
      desktopQuery,
      'change',
      handleDesktopChange,
    );

    if (
      menuToggle &&
      mobileMenu &&
      menuOverlay
    ) {
      listen(
        menuToggle,
        'click',
        handleMenuToggle,
      );

      listen(
        menuOverlay,
        'click',
        () => closeMenu(),
      );

      mobileLinks.forEach((link) => {
        listen(
          link,
          'click',
          () =>
            closeMenu({
              restoreFocus: true,
            }),
        );
      });
    }

    if (
      'IntersectionObserver' in window &&
      observedSections.length
    ) {
      intersectionObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              sectionVisibility.set(
                entry.target,
                entry,
              );
            });

            updateActiveFromObserver();
          },
          {
            rootMargin:
              '-20% 0px -55% 0px',
            threshold: [
              0,
              0.15,
              0.4,
              0.6,
            ],
          },
        );

      observedSections.forEach(
        (section) => {
          intersectionObserver.observe(
            section,
          );
        },
      );
    } else {
      listen(
        window,
        'scroll',
        updateActiveFromPosition,
        {
          passive: true,
        },
      );

      listen(
        window,
        'resize',
        updateActiveFromPosition,
      );

      updateActiveFromPosition();
    }

    updateScrolledState();

    return {
      destroy() {
        closeMenu();

        if (scrollFrame !== null) {
          window.cancelAnimationFrame(
            scrollFrame,
          );
        }

        intersectionObserver?.disconnect();
        destroyListeners();

        navLinks.forEach((link) => {
          link.classList.remove(
            'is-active',
          );

          link.removeAttribute(
            'aria-current',
          );
        });
      },
    };
  };

  /* ==========================================================================
     HERO
     ========================================================================== */

  const createHeroController = (root) => {
    const hero = root.querySelector(
      '[data-dotd-universe-hero]',
    );

    const world = root.querySelector(
      '[data-dotd-universe-world]',
    );

    if (!hero || !world) return null;

    const {
      listen,
      destroy: destroyListeners,
    } = createListenerRegistry();

    const stage = hero.querySelector(
      '.dotd-universe-hero__stage',
    );

    const loadDeferredHeroAssets = () => {
      hero
        .querySelectorAll(
          '[data-hero-src]',
        )
        .forEach((image) => {
          if (image.src) return;

          image.src =
            image.dataset.heroSrc || '';

          image.removeAttribute(
            'data-hero-src',
          );
        });

      hero
        .querySelectorAll(
          '[data-hero-srcset]',
        )
        .forEach((source) => {
          if (source.srcset) return;

          source.srcset =
            source.dataset.heroSrcset || '';

          source.removeAttribute(
            'data-hero-srcset',
          );
        });
    };

    let scrollFrame = null;
    let heroTop = 0;
    let heroHeight = 1;
    let stageHeight = 1;
    let transitionStart = 0;
    let transitionEnd = 1;
    let heroIsActive = true;
    let heroObserver = null;
    let deferredHeroAssetsLoaded = false;

    hero.setAttribute(
      'data-dotd-universe-hero-initialized',
      'true',
    );

    hero.setAttribute(
      'data-hero-fragments-mode',
      'auto',
    );

    const setRootVariable = (
      name,
      value,
    ) => {
      root.style.setProperty(
        name,
        Number(value).toFixed(4),
      );
    };

    const measure = () => {
      const scrollTop = getScrollTop();
      const heroBounds =
        hero.getBoundingClientRect();

      heroTop =
        heroBounds.top + scrollTop;

      heroHeight =
        heroBounds.height;

      stageHeight =
        stage?.getBoundingClientRect()
          .height ||
        window.visualViewport?.height ||
        window.innerHeight ||
        1;

      const viewportHeight =
        window.visualViewport?.height ||
        window.innerHeight ||
        stageHeight;

      const availableTransition =
        Math.max(
          1,
          heroHeight -
            Math.min(
              stageHeight,
              viewportHeight,
            ),
        );

      transitionStart = heroTop;

      transitionEnd =
        heroTop +
        availableTransition;

      requestUpdate();
    };

    const update = () => {
      scrollFrame = null;

      const scrollTop =
        getScrollTop();

      const reducedMotion =
        reducedMotionQuery.matches;

      const progress =
        reducedMotion
          ? 1
          : clamp(
              (
                scrollTop -
                transitionStart
              ) /
                Math.max(
                  1,
                  transitionEnd -
                    transitionStart,
                ),
            );

      if (
        !deferredHeroAssetsLoaded &&
        (
          reducedMotion ||
          progress >= 0.03
        )
      ) {
        deferredHeroAssetsLoaded = true;
        loadDeferredHeroAssets();
      }

      /*
        Timeline

        0.00–0.08
        Scene 1：HARUを短く保持

        0.08–0.20
        HARU → COLOR

        0.20–0.52
        Scene 2：COLORをしっかり保持

        0.52–0.64
        COLORの余韻を残しながら
        低アングル背景を出す

        0.60–0.74
        Scene 3の全身キャラクターを出す

        0.72–0.82
        最後にタイトルを出す

        0.82–0.91
        Scene 3完成状態を保持

        0.91–1.00
        HERO → THE UNIVERSE
      */

      const sceneOneExit = smooth(
        range(
          progress,
          0.08,
          0.2,
        ),
      );

      const sceneTwoEnter = smooth(
        range(
          progress,
          0.08,
          0.2,
        ),
      );

      /*
        Scene2の巨大なCOLORをすぐには消さず、
        Scene3の背景と重ねながらゆっくり退場させる。
      */
      const sceneTwoExit = smooth(
        range(
          progress,
          0.52,
          0.7,
        ),
      );

      /*
        Scene3のレイヤー全体を先に開く。
        この段階では背景が中心で、
        キャラクターとタイトルはまだ薄い。
      */
      const sceneThreeEnter = smooth(
        range(
          progress,
          0.52,
          0.64,
        ),
      );

      /*
        低アングル背景をScene3の最初に出す。
      */
      const lowangleBackgroundEnter = smooth(
        range(
          progress,
          0.52,
          0.64,
        ),
      );

      /*
        背景が見え始めたあと、
        HARUとCOLORの全身を出す。
      */
      const finalCharactersEnter = smooth(
        range(
          progress,
          0.6,
          0.74,
        ),
      );

      /*
        タイトルは最後に出す。
      */
      const finalTypeEnter = smooth(
        range(
          progress,
          0.72,
          0.82,
        ),
      );

      /*
        Scene3の余韻ある退場

        0.78–0.86
        COLORがゆっくり消える

        0.84–0.92
        HARUがゆっくり消える

        0.92–0.955
        タイポグラフィーだけを保持

        0.955–0.985
        タイポグラフィーがゆっくり消える

        0.96–1.00
        背景が最後に白へ溶ける
      */

      const finalColorExit = smooth(
        range(
          progress,
          0.78,
          0.86,
        ),
      );

      const finalHaruExit = smooth(
        range(
          progress,
          0.84,
          0.92,
        ),
      );

      const typeExit = smooth(
        range(
          progress,
          0.92,
          0.97,
        ),
      );

      const backgroundExit = smooth(
        range(
          progress,
          0.94,
          1,
        ),
      );

      const heroExit = backgroundExit;

      const universeEnter = smooth(
        range(
          progress,
          0.97,
          1,
        ),
      );

      const universeHeading = smooth(
        range(
          progress,
          0.94,
          0.985,
        ),
      );

      const universeText = smooth(
        range(
          progress,
          0.965,
          1,
        ),
      );

      const sceneOneOpacity =
        1 - sceneOneExit;

      const sceneTwoOpacity =
        sceneTwoEnter *
        (1 - sceneTwoExit);

      const sceneThreeOpacity =
        sceneThreeEnter;

      const sceneOneScale =
        1.04 -
        0.04 *
          smooth(
            range(
              progress,
              0,
              0.27,
            ),
          );

      const sceneTwoScale =
        1.04 -
        0.04 *
          smooth(
            range(
              progress,
              0.28,
              0.55,
            ),
          );

      const finalTypeOpacity =
        reducedMotion
          ? 1
          : finalTypeEnter *
            (1 - typeExit);

      const finalHaruOpacity =
        reducedMotion
          ? 1
          : finalCharactersEnter *
            (1 - finalHaruExit);

      const finalColorOpacity =
        reducedMotion
          ? 1
          : finalCharactersEnter *
            (1 - finalColorExit);

      const lowangleBackgroundOpacity =
        reducedMotion
          ? 1
          : lowangleBackgroundEnter *
            (1 - backgroundExit);

      setRootVariable(
        '--hero-progress',
        progress,
      );

      setRootVariable(
        '--hero-scene-1',
        reducedMotion
          ? 0
          : sceneOneOpacity,
      );

      setRootVariable(
        '--hero-scene-2',
        reducedMotion
          ? 0
          : sceneTwoOpacity,
      );

      setRootVariable(
        '--hero-scene-3',
        reducedMotion
          ? 1
          : sceneThreeOpacity,
      );

      setRootVariable(
        '--hero-scene-1-scale',
        sceneOneScale,
      );

      setRootVariable(
        '--hero-scene-2-scale',
        sceneTwoScale,
      );

      setRootVariable(
        '--hero-final-type',
        finalTypeOpacity,
      );

      setRootVariable(
        '--hero-final-haru',
        finalHaruOpacity,
      );

      setRootVariable(
        '--hero-final-color',
        finalColorOpacity,
      );

      setRootVariable(
        '--hero-lowangle-background',
        lowangleBackgroundOpacity,
      );

      setRootVariable(
        '--hero-exit',
        reducedMotion
          ? 0
          : heroExit,
      );

      setRootVariable(
        '--universe-enter',
        reducedMotion
          ? 1
          : universeEnter,
      );

      setRootVariable(
        '--universe-heading-opacity',
        reducedMotion
          ? 1
          : universeHeading,
      );

      setRootVariable(
        '--universe-text-opacity',
        reducedMotion
          ? 1
          : universeText,
      );

      hero.toggleAttribute(
        'data-hero-transitioning',
        progress > 0.01 &&
          progress < 0.999,
      );
    };

    function requestUpdate() {
      if (
        !heroIsActive ||
        scrollFrame !== null
      ) {
        return;
      }

      scrollFrame =
        window.requestAnimationFrame(
          update,
        );
    }

    const handleMotionChange = () => {
      updateMotionPreference(root);
      measure();
    };

    if ('IntersectionObserver' in window) {
      heroObserver =
        new IntersectionObserver(
          (entries) => {
            const entry =
              entries[0];

            heroIsActive =
              entry?.isIntersecting ??
              true;

            hero.toggleAttribute(
              'data-hero-active',
              heroIsActive,
            );

            if (heroIsActive) {
              requestUpdate();
            }
          },
          {
            root: null,
            rootMargin:
              '100% 0px 100% 0px',
            threshold: 0,
          },
        );

      heroObserver.observe(hero);
    } else {
      hero.setAttribute(
        'data-hero-active',
        '',
      );
    }

    listen(
      window,
      'scroll',
      requestUpdate,
      {
        passive: true,
      },
    );

    listen(
      window,
      'resize',
      measure,
    );

    listen(
      window,
      'orientationchange',
      measure,
    );

    listen(
      window.visualViewport,
      'resize',
      measure,
    );

    listen(
      reducedMotionQuery,
      'change',
      handleMotionChange,
    );

    hero.setAttribute(
      'data-hero-active',
      '',
    );

    measure();

    return {
      destroy() {
        heroObserver?.disconnect();
        heroObserver = null;

        destroyListeners();

        if (scrollFrame !== null) {
          window.cancelAnimationFrame(
            scrollFrame,
          );
        }

        [
          '--hero-progress',
          '--hero-scene-1',
          '--hero-scene-2',
          '--hero-scene-3',
          '--hero-scene-1-scale',
          '--hero-scene-2-scale',
          '--hero-final-type',
          '--hero-final-haru',
          '--hero-final-color',
          '--hero-lowangle-background',
          '--hero-exit',
          '--universe-enter',
          '--universe-heading-opacity',
          '--universe-text-opacity',
        ].forEach((property) => {
          root.style.removeProperty(
            property,
          );
        });

        hero.removeAttribute(
          'data-hero-transitioning',
        );

        hero.removeAttribute(
          'data-hero-active',
        );

        hero.removeAttribute(
          'data-hero-fragments-mode',
        );

        hero.removeAttribute(
          'data-dotd-universe-hero-initialized',
        );
      },
    };
  };

  /* ==========================================================================
     Initialization
     ========================================================================== */

  const initializeHeader = (root) => {
    const state =
      controllers.get(root) || {};

    state.headerController?.destroy();

    const headerController =
      createHeaderController(root);

    controllers.set(root, {
      ...state,
      headerController,
    });
  };

  const initializeHero = (root) => {
    const state =
      controllers.get(root) || {};

    state.heroController?.destroy();

    const heroController =
      createHeroController(root);

    controllers.set(root, {
      ...state,
      heroController,
    });
  };

  const resolveRoot = (
    scope = document,
  ) => {
    if (
      scope?.matches?.(
        ROOT_SELECTOR,
      )
    ) {
      return scope;
    }

    return (
      scope?.querySelector?.(
        ROOT_SELECTOR,
      ) ||
      scope?.closest?.(
        ROOT_SELECTOR,
      ) ||
      document.querySelector(
        ROOT_SELECTOR,
      )
    );
  };

  const initialize = (
    scope = document,
  ) => {
    const root =
      resolveRoot(scope);

    if (!root) return;

    if (
      !root.hasAttribute(
        INITIALIZED_ATTRIBUTE,
      )
    ) {
      root.setAttribute(
        INITIALIZED_ATTRIBUTE,
        'true',
      );

      updateMotionPreference(root);

      const handleMotionChange =
        () => {
          updateMotionPreference(root);
        };

      reducedMotionQuery
        .addEventListener?.(
          'change',
          handleMotionChange,
        );

      controllers.set(root, {
        destroyMotionListener:
          () => {
            reducedMotionQuery
              .removeEventListener?.(
                'change',
                handleMotionChange,
              );
          },
      });
    }

    initializeHeader(root);
    initializeHero(root);
  };

  const destroy = (scope) => {
    const root =
      resolveRoot(scope);

    if (!root) return;

    const state =
      controllers.get(root);

    if (!state) return;

    const containsHeader =
      scope?.matches?.(
        '[data-dotd-universe-header]',
      ) ||
      scope?.querySelector?.(
        '[data-dotd-universe-header]',
      );

    const containsHero =
      scope?.matches?.(
        '[data-dotd-universe-hero]',
      ) ||
      scope?.querySelector?.(
        '[data-dotd-universe-hero]',
      );

    if (containsHeader) {
      state.headerController?.destroy();
      state.headerController = null;
    }

    if (containsHero) {
      state.heroController?.destroy();
      state.heroController = null;
    }

    if (scope === root) {
      state.headerController?.destroy();
      state.heroController?.destroy();
      state.destroyMotionListener?.();

      controllers.delete(root);

      root.removeAttribute(
        INITIALIZED_ATTRIBUTE,
      );
    } else {
      controllers.set(
        root,
        state,
      );
    }
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      {
        once: true,
      },
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(event.target);
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroy(event.target);
    },
  );
})();

/* ==========================================================
   Universe fragment animation
========================================================== */

(() => {
  'use strict';

  let universeFragmentObserver = null;

  const initializeUniverseFragments = (scope = document) => {
    const root =
      scope?.matches?.('[data-dotd-universe-root]')
        ? scope
        : scope?.querySelector?.('[data-dotd-universe-root]') ||
          document.querySelector('[data-dotd-universe-root]');

    if (!root) return;

    const worldSection = root.querySelector(
      '[data-dotd-universe-world]',
    );

    if (!worldSection) return;

    universeFragmentObserver?.disconnect();

    if (!('IntersectionObserver' in window)) {
      worldSection.classList.add('is-active');
      return;
    }

    universeFragmentObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(
            'is-active',
            entry.isIntersecting,
          );
        });
      },
      {
        threshold: 0.05,
        rootMargin: '10% 0px 10% 0px',
      },
    );

    universeFragmentObserver.observe(worldSection);
  };

  const start = () => {
    initializeUniverseFragments(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true },
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeUniverseFragments(event.target);
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    () => {
      universeFragmentObserver?.disconnect();
      universeFragmentObserver = null;
    },
  );
})();

/* ==========================================================================
   CHARACTERS slider + shared profile component
   PC: buttons / keyboard
   Mobile: native horizontal swipe
   Profile: one shared dialog populated from character templates

   PROFILE PORTAL:
   The shared profile dialog is moved outside the carousel and mounted
   directly under the Universe root so viewport-fixed positioning is not
   clipped by CHARACTERS overflow containers.
   ========================================================================== */

(() => {
  'use strict';

  const sliderControllers = new WeakMap();
  const desktopQuery = window.matchMedia('(min-width: 750px)');

  const createCharactersSlider = (slider) => {
    if (!slider || sliderControllers.has(slider)) return;

    const viewport = slider.querySelector(
      '.dotd-universe-characters__viewport',
    );

    const track = slider.querySelector(
      '[data-dotd-character-track]',
    );

    const slides = [
      ...slider.querySelectorAll(
        '[data-dotd-character-slide]',
      ),
    ];

    const previousButton = slider.querySelector(
      '[data-dotd-character-previous]',
    );

    const nextButton = slider.querySelector(
      '[data-dotd-character-next]',
    );

    const counter = slider.querySelector(
      '[data-dotd-character-counter]',
    );

    const dots = [
      ...slider.querySelectorAll(
        '[data-dotd-character-dot]',
      ),
    ];

    const profileOpenButtons = [
      ...slider.querySelectorAll(
        '[data-dotd-character-profile-open]',
      ),
    ];

    const sharedProfile = slider.querySelector(
      '[data-dotd-character-profile]',
    );

    const universeRoot = slider.closest(
      '[data-dotd-universe-root]',
    );

    /*
      Save the original profile location before moving it.

      This is used only when Shopify destroys / reloads the section,
      so the DOM can be restored cleanly.
    */
    const profileOriginalParent =
      sharedProfile?.parentNode || null;

    const profileOriginalNextSibling =
      sharedProfile?.nextSibling || null;

    /*
      PROFILE PORTAL

      The profile originally lives inside the CHARACTERS carousel.
      That carousel and the CHARACTERS section use overflow:hidden.

      On mobile, this causes a position:fixed profile to still be clipped
      at the bottom edge of the CHARACTERS section.

      Move only the shared profile dialog to the Universe root.
      This makes the fixed overlay independent from the carousel.
    */
    if (
      sharedProfile &&
      universeRoot &&
      sharedProfile.parentNode !== universeRoot
    ) {
      universeRoot.appendChild(sharedProfile);
    }

    const profileStage = sharedProfile?.querySelector(
      '.dotd-universe-characters__profile-stage',
    );

    const profileMount = sharedProfile?.querySelector(
      '[data-dotd-character-profile-mount]',
    );

    const profileGuide = slider.querySelector(
      '[data-dotd-character-profile-guide]',
    );

    const profileTemplates = new Map(
      [
        ...slider.querySelectorAll(
          'template[data-dotd-character-profile-template]',
        ),
      ].map((template) => [
        template.dataset.dotdCharacterProfileTemplate,
        template,
      ]),
    );

    if (!viewport || !track || !slides.length) return;

    let currentIndex = 0;
    let scrollFrame = null;
    let intersectionObserver = null;
    let activeProfileTrigger = null;
    let profileGuideTimer = null;

    /*
      PROFILE SCROLL FIX

      Save the page position when profile opens and reset every
      profile scroll container to the top.

      Mobile:
      .dotd-universe-characters__profile-stage

      Desktop:
      .dotd-universe-characters__profile-card
    */
    let pageScrollY = 0;

    const cleanups = [];

    const listen = (
      target,
      eventName,
      handler,
      options,
    ) => {
      if (!target?.addEventListener) return;

      target.addEventListener(
        eventName,
        handler,
        options,
      );

      cleanups.push(() => {
        target.removeEventListener(
          eventName,
          handler,
          options,
        );
      });
    };

    const getCharacterKey = (element) =>
      element?.dataset?.dotdCharacterKey || '';

    const isProfileOpen = () =>
      Boolean(
        sharedProfile?.classList.contains(
          'is-open',
        ),
      );

    const setProfileState = (isOpen) => {
      if (!sharedProfile) return;

      sharedProfile.classList.toggle(
        'is-open',
        isOpen,
      );

      sharedProfile.setAttribute(
        'aria-hidden',
        isOpen ? 'false' : 'true',
      );
    };

    const setProfileLabel = () => {
      if (!sharedProfile) return;

      const title = sharedProfile.querySelector(
        '[data-dotd-character-profile-title]',
      );

      if (title?.id) {
        sharedProfile.setAttribute(
          'aria-labelledby',
          title.id,
        );
      } else {
        sharedProfile.removeAttribute(
          'aria-labelledby',
        );
      }
    };

    const resetProfileScroll = () => {
      if (!sharedProfile) return;

      sharedProfile.scrollTop = 0;

      if (profileStage) {
        profileStage.scrollTop = 0;
      }

      const profileCard =
        sharedProfile.querySelector(
          '.dotd-universe-characters__profile-card',
        );

      if (profileCard) {
        profileCard.scrollTop = 0;
      }
    };

    const focusWithoutScroll = (element) => {
      if (!element?.focus) return;

      try {
        element.focus({
          preventScroll: true,
        });
      } catch (error) {
        element.focus();
        resetProfileScroll();
      }
    };

    const closeProfile = ({
      restoreFocus = true,
      restoreScroll = true,
    } = {}) => {
      if (!isProfileOpen()) return;

      const scrollToRestore = pageScrollY;
      const triggerToRestore =
        activeProfileTrigger;

      setProfileState(false);

      slider.classList.remove(
        'is-profile-open',
      );

      document.body.classList.remove(
        'dotd-character-profile-open',
      );

      profileOpenButtons.forEach((button) => {
        button.setAttribute(
          'aria-expanded',
          'false',
        );
      });

      activeProfileTrigger = null;

      resetProfileScroll();

      window.requestAnimationFrame(() => {
        if (restoreScroll) {
          window.scrollTo({
            top: scrollToRestore,
            left: 0,
            behavior: 'auto',
          });
        }

        if (restoreFocus) {
          focusWithoutScroll(
            triggerToRestore,
          );
        }

        if (restoreScroll) {
          window.requestAnimationFrame(() => {
            window.scrollTo({
              top: scrollToRestore,
              left: 0,
              behavior: 'auto',
            });
          });
        }
      });
    };

    const bindProfileCloseButtons = () => {
      if (!sharedProfile) return;

      [
        ...sharedProfile.querySelectorAll(
          '[data-dotd-character-profile-close]',
        ),
      ].forEach((button) => {
        if (
          button.hasAttribute(
            'data-dotd-character-profile-close-bound',
          )
        ) {
          return;
        }

        button.setAttribute(
          'data-dotd-character-profile-close-bound',
          'true',
        );

        listen(button, 'click', () => {
          closeProfile();
        });
      });
    };

    const renderProfile = (
      characterKey,
    ) => {
      if (
        !sharedProfile ||
        !profileMount ||
        !characterKey
      ) {
        return false;
      }

      const template =
        profileTemplates.get(characterKey);

      if (template) {
        profileMount.replaceChildren(
          template.content.cloneNode(true),
        );
      } else {
        const currentContent =
          profileMount.querySelector(
            '[data-dotd-character-profile-content]',
          );

        if (
          getCharacterKey(currentContent) !==
          characterKey
        ) {
          return false;
        }
      }

      sharedProfile.dataset.dotdCharacterKey =
        characterKey;

      setProfileLabel();
      bindProfileCloseButtons();
      resetProfileScroll();

      return true;
    };

    const openProfile = (trigger) => {
      if (!sharedProfile) return;

      const characterKey =
        getCharacterKey(trigger);

      if (!characterKey) return;

      if (!isProfileOpen()) {
        pageScrollY =
          window.scrollY ??
          document.scrollingElement?.scrollTop ??
          document.documentElement.scrollTop ??
          0;
      }

      if (isProfileOpen()) {
        closeProfile({
          restoreFocus: false,
          restoreScroll: false,
        });
      }

      if (!renderProfile(characterKey)) {
        return;
      }

      activeProfileTrigger = trigger;

      resetProfileScroll();

      slider.classList.add(
        'is-profile-open',
      );

      document.body.classList.add(
        'dotd-character-profile-open',
      );

      profileOpenButtons.forEach((button) => {
        button.setAttribute(
          'aria-expanded',
          button === trigger
            ? 'true'
            : 'false',
        );
      });

      setProfileState(true);

      window.requestAnimationFrame(() => {
        resetProfileScroll();

        window.requestAnimationFrame(() => {
          resetProfileScroll();

          const closeButton =
            sharedProfile.querySelector(
              '.dotd-universe-characters__profile-close',
            );

          focusWithoutScroll(
            closeButton,
          );

          resetProfileScroll();
        });
      });
    };

    const handleProfileKeyDown = (
      event,
    ) => {
      if (
        !isProfileOpen() ||
        !sharedProfile
      ) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();

        closeProfile();

        return;
      }

      if (event.key !== 'Tab') return;

      const focusableItems = [
        ...sharedProfile.querySelectorAll(
          [
            'button:not([disabled])',
            'a[href]',
            '[tabindex]:not([tabindex="-1"])',
          ].join(','),
        ),
      ].filter(
        (item) =>
          !item.hidden &&
          item.offsetParent !== null,
      );

      if (!focusableItems.length) {
        event.preventDefault();
        return;
      }

      const firstItem =
        focusableItems[0];

      const lastItem =
        focusableItems[
          focusableItems.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstItem
      ) {
        event.preventDefault();

        focusWithoutScroll(lastItem);
      } else if (
        !event.shiftKey &&
        document.activeElement === lastItem
      ) {
        event.preventDefault();

        focusWithoutScroll(firstItem);
      }
    };

    const showProfileGuide = () => {
      if (
        !profileGuide ||
        window.sessionStorage.getItem(
          'dotd-character-profile-guide-seen',
        ) === 'true'
      ) {
        return;
      }

      window.clearTimeout(
        profileGuideTimer,
      );

      profileGuide.classList.add(
        'is-visible',
      );

      profileGuideTimer =
        window.setTimeout(() => {
          profileGuide.classList.remove(
            'is-visible',
          );

          window.sessionStorage.setItem(
            'dotd-character-profile-guide-seen',
            'true',
          );
        }, 3400);
    };

    const formatNumber = (value) =>
      String(value).padStart(2, '0');

    const updateState = (index) => {
      currentIndex = Math.max(
        0,
        Math.min(
          index,
          slides.length - 1,
        ),
      );

      slides.forEach(
        (slide, slideIndex) => {
          const isActive =
            slideIndex === currentIndex;

          slide.classList.toggle(
            'is-active',
            isActive,
          );

          slide.setAttribute(
            'aria-hidden',
            isActive
              ? 'false'
              : 'true',
          );
        },
      );

      dots.forEach(
        (dot, dotIndex) => {
          const isActive =
            dotIndex === currentIndex;

          dot.classList.toggle(
            'is-active',
            isActive,
          );

          if (isActive) {
            dot.setAttribute(
              'aria-current',
              'true',
            );
          } else {
            dot.removeAttribute(
              'aria-current',
            );
          }
        },
      );

      if (previousButton) {
        previousButton.disabled =
          currentIndex === 0;
      }

      if (nextButton) {
        nextButton.disabled =
          currentIndex ===
          slides.length - 1;
      }

      if (counter) {
        counter.textContent =
          `${formatNumber(
            currentIndex + 1,
          )} / ` +
          `${formatNumber(
            slides.length,
          )}`;
      }
    };

    const moveTo = (
      index,
      {
        smooth = true,
      } = {},
    ) => {
      if (isProfileOpen()) return;

      const nextIndex = Math.max(
        0,
        Math.min(
          index,
          slides.length - 1,
        ),
      );

      updateState(nextIndex);

      if (desktopQuery.matches) {
        track.style.transform =
          `translate3d(-${
            nextIndex * 100
          }%, 0, 0)`;

        return;
      }

      viewport.scrollTo({
        left:
          slides[nextIndex].offsetLeft,
        behavior: smooth
          ? 'smooth'
          : 'auto',
      });
    };

    const updateMobileIndex = () => {
      scrollFrame = null;

      if (desktopQuery.matches) {
        return;
      }

      const viewportLeft =
        viewport.scrollLeft;

      let nearestIndex = 0;
      let nearestDistance = Infinity;

      slides.forEach(
        (slide, index) => {
          const distance = Math.abs(
            slide.offsetLeft -
              viewportLeft,
          );

          if (
            distance <
            nearestDistance
          ) {
            nearestDistance =
              distance;

            nearestIndex = index;
          }
        },
      );

      if (
        nearestIndex !== currentIndex
      ) {
        updateState(nearestIndex);
      }
    };

    const requestMobileUpdate = () => {
      if (scrollFrame !== null) {
        return;
      }

      scrollFrame =
        window.requestAnimationFrame(
          updateMobileIndex,
        );
    };

    const handleKeyboard = (
      event,
    ) => {
      if (isProfileOpen()) return;

      if (
        event.key === 'ArrowLeft'
      ) {
        event.preventDefault();

        moveTo(currentIndex - 1);
      }

      if (
        event.key === 'ArrowRight'
      ) {
        event.preventDefault();

        moveTo(currentIndex + 1);
      }

      if (event.key === 'Home') {
        event.preventDefault();

        moveTo(0);
      }

      if (event.key === 'End') {
        event.preventDefault();

        moveTo(
          slides.length - 1,
        );
      }
    };

    const handleBreakpointChange =
      () => {
        if (desktopQuery.matches) {
          viewport.scrollTo({
            left: 0,
            behavior: 'auto',
          });

          track.style.transform =
            `translate3d(-${
              currentIndex * 100
            }%, 0, 0)`;
        } else {
          track.style.removeProperty(
            'transform',
          );

          window.requestAnimationFrame(
            () => {
              viewport.scrollTo({
                left:
                  slides[
                    currentIndex
                  ].offsetLeft,
                behavior: 'auto',
              });
            },
          );
        }

        updateState(currentIndex);

        if (isProfileOpen()) {
          window.requestAnimationFrame(
            resetProfileScroll,
          );
        }
      };

    listen(
      previousButton,
      'click',
      () => {
        moveTo(currentIndex - 1);
      },
    );

    listen(
      nextButton,
      'click',
      () => {
        moveTo(currentIndex + 1);
      },
    );

    dots.forEach((dot) => {
      listen(dot, 'click', () => {
        const index = Number(
          dot.dataset.dotdCharacterDot,
        );

        if (
          Number.isFinite(index)
        ) {
          moveTo(index);
        }
      });
    });

    profileOpenButtons.forEach(
      (button) => {
        listen(
          button,
          'click',
          () => {
            profileGuide?.classList.remove(
              'is-visible',
            );

            window.sessionStorage.setItem(
              'dotd-character-profile-guide-seen',
              'true',
            );

            openProfile(button);
          },
        );
      },
    );

    bindProfileCloseButtons();

    listen(
      document,
      'keydown',
      handleProfileKeyDown,
    );

    listen(
      viewport,
      'scroll',
      requestMobileUpdate,
      {
        passive: true,
      },
    );

    listen(
      slider,
      'keydown',
      handleKeyboard,
    );

    listen(
      desktopQuery,
      'change',
      handleBreakpointChange,
    );

    if (
      'IntersectionObserver' in
      window
    ) {
      intersectionObserver =
        new IntersectionObserver(
          (entries) => {
            entries.forEach(
              (entry) => {
                if (
                  entry.isIntersecting &&
                  entry.intersectionRatio >=
                    0.35
                ) {
                  slider.classList.add(
                    'is-visible',
                  );

                  updateState(
                    currentIndex,
                  );
                }
              },
            );
          },
          {
            threshold: [
              0.1,
              0.35,
              0.65,
            ],
          },
        );

      intersectionObserver.observe(
        slider,
      );
    } else {
      slider.classList.add(
        'is-visible',
      );
    }

    updateState(0);
    handleBreakpointChange();
    setProfileLabel();

    window.setTimeout(
      showProfileGuide,
      900,
    );

    slider.setAttribute(
      'data-dotd-character-slider-initialized',
      'true',
    );

    sliderControllers.set(
      slider,
      {
        destroy() {
          closeProfile({
            restoreFocus: false,
          });

          window.clearTimeout(
            profileGuideTimer,
          );

          profileGuide?.classList.remove(
            'is-visible',
          );

          cleanups
            .splice(0)
            .forEach(
              (cleanup) =>
                cleanup(),
            );

          intersectionObserver?.disconnect();

          if (
            scrollFrame !== null
          ) {
            window.cancelAnimationFrame(
              scrollFrame,
            );
          }

          track.style.removeProperty(
            'transform',
          );

          slides.forEach(
            (slide) => {
              slide.classList.remove(
                'is-active',
              );

              slide.removeAttribute(
                'aria-hidden',
              );
            },
          );

          slider.removeAttribute(
            'data-dotd-character-slider-initialized',
          );

          /*
            Restore profile to its original location when Shopify
            unloads or re-renders the section.
          */
          if (
            sharedProfile &&
            profileOriginalParent
          ) {
            if (
              profileOriginalNextSibling &&
              profileOriginalNextSibling.parentNode ===
                profileOriginalParent
            ) {
              profileOriginalParent.insertBefore(
                sharedProfile,
                profileOriginalNextSibling,
              );
            } else {
              profileOriginalParent.appendChild(
                sharedProfile,
              );
            }
          }

          sliderControllers.delete(
            slider,
          );
        },
      },
    );
  };

  const initializeCharactersSliders = (
    scope = document,
  ) => {
    const sliders = [];

    if (
      scope?.matches?.(
        '[data-dotd-character-slider]',
      )
    ) {
      sliders.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-character-slider]',
      )
      .forEach((slider) => {
        sliders.push(slider);
      });

    sliders.forEach(
      createCharactersSlider,
    );
  };

  const destroyCharactersSliders = (
    scope,
  ) => {
    const sliders = [];

    if (
      scope?.matches?.(
        '[data-dotd-character-slider]',
      )
    ) {
      sliders.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-character-slider]',
      )
      .forEach((slider) => {
        sliders.push(slider);
      });

    sliders.forEach((slider) => {
      sliderControllers
        .get(slider)
        ?.destroy();
    });
  };

  const start = () => {
    initializeCharactersSliders(
      document,
    );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      },
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeCharactersSliders(
        event.target,
      );
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyCharactersSliders(
        event.target,
      );
    },
  );
})();


/* ==========================================================================
   STORIES
   Featured reveal / trailer reveal / Shopify section lifecycle
   ========================================================================== */

(() => {
  'use strict';

  const storyControllers = new WeakMap();
  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  );

  const createStoriesController = (section) => {
    if (!section || storyControllers.has(section)) return;

    const featured = section.querySelector(
      '.dotd-universe-stories__featured',
    );

    const revealItems = [
      ...section.querySelectorAll(
        [
          '.dotd-universe-stories__trailer-item',
          '.dotd-universe-stories__episode-card',
          '.dotd-universe-stories__beyond-item',
        ].join(','),
      ),
    ];

    const disabledLinks = [
      ...section.querySelectorAll(
        'a[aria-disabled="true"]',
      ),
    ];

    let observer = null;

    const preventDisabledLink = (event) => {
      event.preventDefault();
    };

    revealItems.forEach((item) => {
      item.setAttribute(
        'data-story-reveal',
        '',
      );
    });

    disabledLinks.forEach((link) => {
      link.addEventListener(
        'click',
        preventDisabledLink,
      );
    });

    const revealImmediately = () => {
      featured?.classList.add('is-visible');

      revealItems.forEach((item) => {
        item.classList.add('is-visible');
      });
    };

    if (
      'IntersectionObserver' in window &&
      !reducedMotionQuery.matches
    ) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(
              'is-visible',
            );

            observer?.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -8% 0px',
        },
      );

      if (featured) {
        observer.observe(featured);
      }

      revealItems.forEach((item) => {
        observer.observe(item);
      });
    } else {
      revealImmediately();
    }

    section.setAttribute(
      'data-dotd-stories-initialized',
      'true',
    );

    storyControllers.set(section, {
      destroy() {
        observer?.disconnect();

        disabledLinks.forEach((link) => {
          link.removeEventListener(
            'click',
            preventDisabledLink,
          );
        });

        revealItems.forEach((item) => {
          item.removeAttribute(
            'data-story-reveal',
          );

          item.classList.remove(
            'is-visible',
          );
        });

        featured?.classList.remove(
          'is-visible',
        );

        section.removeAttribute(
          'data-dotd-stories-initialized',
        );

        storyControllers.delete(section);
      },
    });
  };

  const initializeStories = (
    scope = document,
  ) => {
    const sections = [];

    if (
      scope?.matches?.(
        '[data-dotd-stories]',
      )
    ) {
      sections.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-stories]',
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach(
      createStoriesController,
    );
  };

  const destroyStories = (scope) => {
    const sections = [];

    if (
      scope?.matches?.(
        '[data-dotd-stories]',
      )
    ) {
      sections.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-stories]',
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      storyControllers
        .get(section)
        ?.destroy();
    });
  };

  const start = () => {
    initializeStories(document);
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      },
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeStories(event.target);
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyStories(event.target);
    },
  );
})();

/* ==========================================================================
   FOOTER
   Lightweight one-time reveal / Shopify section lifecycle
   ========================================================================== */

(() => {
  'use strict';

  const footerControllers = new WeakMap();

  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  );

  const createFooterController = (footer) => {
    if (
      !footer ||
      footerControllers.has(footer)
    ) {
      return;
    }

    const revealItems = [
      ...footer.querySelectorAll(
        '[data-dotd-footer-reveal]',
      ),
    ];

    if (!revealItems.length) return;

    let observer = null;
    const revealTimers = [];

    const revealImmediately = () => {
      footer.removeAttribute(
        'data-footer-motion',
      );

      revealItems.forEach((item) => {
        item.classList.add('is-visible');
      });
    };

    const revealWithMotion = () => {
      revealItems.forEach((item, index) => {
        const timer = window.setTimeout(() => {
          item.classList.add('is-visible');
        }, index * 90);

        revealTimers.push(timer);
      });
    };

    if (
      reducedMotionQuery.matches ||
      !('IntersectionObserver' in window)
    ) {
      revealImmediately();
    } else {
      /*
        CSS側で、JSが有効な場合だけ
        フェード前の非表示状態へ切り替えます。
      */
      footer.setAttribute(
        'data-footer-motion',
        'true',
      );

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            revealWithMotion();

            observer?.unobserve(entry.target);
            observer?.disconnect();
            observer = null;
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -8% 0px',
        },
      );

      observer.observe(footer);
    }

    footer.setAttribute(
      'data-dotd-footer-initialized',
      'true',
    );

    footerControllers.set(footer, {
      destroy() {
        observer?.disconnect();

        revealTimers.forEach((timer) => {
          window.clearTimeout(timer);
        });

        revealItems.forEach((item) => {
          item.classList.remove(
            'is-visible',
          );
        });

        footer.removeAttribute(
          'data-footer-motion',
        );

        footer.removeAttribute(
          'data-dotd-footer-initialized',
        );

        footerControllers.delete(footer);
      },
    });
  };

  const initializeFooters = (
    scope = document,
  ) => {
    const footers = [];

    if (
      scope?.matches?.(
        '[data-dotd-universe-footer]',
      )
    ) {
      footers.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-universe-footer]',
      )
      .forEach((footer) => {
        footers.push(footer);
      });

    footers.forEach(
      createFooterController,
    );
  };

  const destroyFooters = (scope) => {
    const footers = [];

    if (
      scope?.matches?.(
        '[data-dotd-universe-footer]',
      )
    ) {
      footers.push(scope);
    }

    scope
      ?.querySelectorAll?.(
        '[data-dotd-universe-footer]',
      )
      .forEach((footer) => {
        footers.push(footer);
      });

    footers.forEach((footer) => {
      footerControllers
        .get(footer)
        ?.destroy();
    });
  };

  const start = () => {
    initializeFooters(document);
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      },
    );
  } else {
    start();
  }

  /*
    Shopifyテーマエディターでセクションが
    再描画された場合にも再初期化します。
  */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeFooters(event.target);
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyFooters(event.target);
    },
  );
})();

/* DOTD GA4 story entry clicks v1 - reuse the Google & YouTube tag. */
(() => {
  'use strict';
  if (location.pathname !== '/pages/dotd-universe' || window.Shopify?.designMode) return;
  if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }
  let previousLink = null;
  let previousTime = 0;
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented) return;
    const url = new URL(link.href, location.href);
    const match = url.pathname.match(/^\/pages\/dotd-webtoon-episode-(\d+)\/?$/);
    if (url.origin !== location.origin || !match) return;
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() !== true) return;
    } catch (_) { return; }
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return;
    const now = Date.now();
    if (previousLink === link && now - previousTime < 1500) return;
    previousLink = link; previousTime = now;
    const params = {
      send_to: 'G-12L8RB065J',
      episode_id: 'ep' + match[1].padStart(2, '0'),
      content_type: 'webtoon',
      transport_type: 'beacon'
    };
    if (typeof window.gtag === 'function') window.gtag('event', 'webtoon_entry_click', params);
    else {
      window.dataLayer = window.dataLayer || [];
      (function () { window.dataLayer.push(arguments); })('event', 'webtoon_entry_click', params);
    }
  });
})();
